import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { isRecord } from '../utils/backendData';

export type AccountOption<TId extends string = string> = { id: TId; name: string };

type TransferResult = { success: boolean; message?: string };

type Props<TId extends string = string> = {
  accounts: AccountOption<TId>[];
  title: string;
  token: string | null;
  onTransfer: (
    token: string | null,
    fromId: TId,
    toId: TId,
    amount: number
  ) => Promise<TransferResult>;
  onSuccess?: () => void;
};

function isAccountOption(value: unknown): value is AccountOption {
  return isRecord(value) && typeof value.id === 'string' && typeof value.name === 'string';
}

export default function FundTransfer<TId extends string = string>({
  accounts,
  title,
  token,
  onTransfer,
  onSuccess,
}: Props<TId>) {
  const safeAccounts = Array.isArray(accounts) ? accounts.filter(isAccountOption) : [];
  const [fromAccountId, setFromAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const amountNum = parseFloat(amount);
  const amountValid = amount !== '' && !isNaN(amountNum) && amountNum > 0;
  const sameAccount = fromAccountId !== '' && fromAccountId === toAccountId;
  const canSubmit = fromAccountId !== '' && toAccountId !== '' && amountValid && !sameAccount;

  const handleTransfer = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await onTransfer(token, fromAccountId as TId, toAccountId as TId, amountNum);

      if (result.success) {
        setSuccess(true);
        setFromAccountId('');
        setAmount('');
        setToAccountId('');
        onSuccess?.();
      } else {
        setError(result.message ?? 'Transfer failed.');
      }
    } catch (err: unknown) {
      const axiosMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(axiosMessage ?? (err instanceof Error ? err.message : 'An unexpected error occurred.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{ p: 4, maxWidth: 1100, width: '100%', borderRadius: '15px', mb: 3 }}
    >
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="center">
          <Chip
            label={title}
            sx={{
              fontWeight: 700,
              letterSpacing: 0.6,
              fontSize: 13,
              background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
              color: 'grey.700',
              borderRadius: '999px',
              px: 1.5,
              py: 2,
            }}
          />
        </Stack>

        {/* Accounts row */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth size="small" disabled={loading}>
              <InputLabel>From Account</InputLabel>
              <Select
                label="From Account"
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
                sx={{ borderRadius: '10px', '& .MuiSelect-select': { textAlign: 'center' } }}
              >
                {safeAccounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', pt: 1.25, color: 'grey.400' }}>
            <ArrowForwardIcon fontSize="large" />
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <FormControl fullWidth size="small" disabled={loading}>
              <InputLabel>To Account</InputLabel>
              <Select
                label="To Account"
                value={toAccountId}
                onChange={(e) => setToAccountId(e.target.value)}
                sx={{ borderRadius: '10px', '& .MuiSelect-select': { textAlign: 'center' } }}
              >
                {safeAccounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {sameAccount && (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '10px', fontSize: 12 }}>
                From and To accounts must be different.
              </Alert>
            )}
          </Box>
        </Box>

        {/* Amount — half width, centered */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TextField
            label="Amount"
            size="small"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={loading}
            error={amount !== '' && !amountValid}
            helperText={amount !== '' && !amountValid ? 'Enter a positive number' : ''}
            inputProps={{ inputMode: 'decimal' }}
            sx={{ width: '50%', '& .MuiOutlinedInput-root': { borderRadius: '10px' }, '& input': { textAlign: 'center' } }}
          />
        </Box>

        {/* Transfer button */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            disabled={!canSubmit || loading}
            onClick={handleTransfer}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 600,
              width: '50%',
              py: 1,
              boxShadow: '0 6px 14px rgba(17, 24, 39, 0.15)',
              position: 'relative',
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" sx={{ position: 'absolute' }} />
            ) : (
              'Transfer'
            )}
          </Button>

          {success && (
            <Alert severity="success" sx={{ borderRadius: '10px', fontSize: 13 }}>
              Transfer successful.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ borderRadius: '10px', fontSize: 13 }}>
              {error}
            </Alert>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
